<template>
    <div class="space-y-3">
        <section class="space-y-1">
            <form-label>Entity</form-label>
            <div v-if="entityWire" class="flex items-center justify-between gap-2 text-xs">
                <button class="truncate text-left hover:text-ui-active" title="Select the entity node"
                    @click="hass.select(page, 'node', entityWire.from.node)">{{ entityId || '(no entity selected)' }}</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the entity" @click="hass.removeWire(page, entityWire.id)">×</button>
            </div>
            <p v-else class="text-xs text-gray-300">The Primary Entity of each object; wire an entity node to compare another.</p>
        </section>

        <form-select name="Compare" :model-value="attributeMode ? 'attribute' : 'state'" @update:model-value="setMode">
            <option value="state">State</option>
            <option value="attribute">Attribute</option>
        </form-select>
        <HassSelector v-if="attributeMode" class="text-sm" v-model="draft.attribute" :settings="settings"
            placeholder="attribute" :selector="{ kind: 'attribute', entityId, preserveUnknown: true }" />

        <section class="space-y-2">
            <form-label>Cases (the first that matches applies)</form-label>
            <div v-for="(item, index) in draft.cases" :key="item.id" class="space-y-0.5">
                <div class="flex items-center gap-1.5">
                    <form-select class="text-sm w-16 flex-shrink-0" v-model="item.operator">
                        <option v-for="op in OPERATORS" :key="op" :value="op">{{ op }}</option>
                    </form-select>
                    <form-input v-model="item.value" type="text" placeholder="value" class="flex-1 text-sm" />
                    <button v-if="index > 0" class="text-xs text-gray-300 hover:text-white" title="Check this case earlier"
                        @click="moveUp(index)">↑</button>
                    <button class="ha-builder-danger-button" title="Remove case and its wires" @click="draft.cases.splice(index, 1)">×</button>
                </div>
                <p v-if="!bindingsByPort[switchCasePort(item.id)]" class="text-xs text-gray-400">Drag a wire from this case to a binding.</p>
                <div v-for="entry in bindingsByPort[switchCasePort(item.id)]" :key="entry.wire.id" class="flex items-center justify-between gap-2 text-xs">
                    <button class="truncate text-left hover:text-ui-active" title="Select the binding"
                        @click="hass.select(page, 'node', entry.node.id)">{{ entry.label }}</button>
                    <button class="text-red-300 hover:text-red-200" title="Disconnect the binding" @click="hass.removeWire(page, entry.wire.id)">×</button>
                </div>
            </div>
            <button class="ha-builder-link-button" @click="addCase">+ case</button>
            <div class="space-y-0.5">
                <p class="text-xs font-semibold text-gray-200">Else</p>
                <p v-if="!bindingsByPort[SWITCH_ELSE_PORT]" class="text-xs text-gray-400">
                    Drag a wire from Else to a binding, or bind the property without a case.
                </p>
                <div v-for="entry in bindingsByPort[SWITCH_ELSE_PORT]" :key="entry.node.id" class="flex items-center justify-between gap-2 text-xs">
                    <button class="truncate text-left hover:text-ui-active" title="Select the binding"
                        @click="hass.select(page, 'node', entry.node.id)">{{ entry.label }}</button>
                    <button v-if="entry.wire" class="text-red-300 hover:text-red-200" title="Disconnect the binding"
                        @click="hass.removeWire(page, entry.wire.id)">×</button>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup>
import { computed, ref, toRaw } from 'vue';
import { useHassFlowStore } from '../../stores/hassFlowStore.js';
import { getHassNodeSchema, SWITCH_ELSE_PORT, switchCasePort } from '../../Hasp/properties/v2/hass/index.js';
import { getBindingCase, getSwitchBindings, getSwitchEntityId, getSwitchFallbackBindings, getWiresInto } from '../../hass/hassGraph.js';
import HassSelector from '../hassbuilder/HassSelector.vue';
import { useNodeDraft } from './useNodeDraft.js';

/**
 * A switch node: the entity it compares (state or one attribute) and its
 * cases in order, each with the bindings wired to it, then Else.
 */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
    settings: { type: Object, default: () => ({}) },
});
const hass = useHassFlowStore();
const OPERATORS = ['==', '!=', '>', '<', '>=', '<='];

const entityWire = computed(() => getWiresInto(props.graph, { node: props.node.id, port: 'entity' })[0] ?? null);
const entityId = computed(() => getSwitchEntityId(props.graph, props.node.id));

const draft = useNodeDraft({
    build: () => ({
        attribute: toRaw(props.node).data.attribute ?? '',
        cases: structuredClone(toRaw(props.node).data.cases ?? []),
    }),
    snapshot: value => value,
    current: () => ({ attribute: props.node.data.attribute ?? '', cases: props.node.data.cases ?? [] }),
    commit: data => hass.setNodeData(props.page, props.node.id, { ...toRaw(props.node).data, ...data }),
});

// "Attribute" stays selected while no attribute is picked yet.
const attributeMode = ref(Boolean(draft.value.attribute));
function setMode(mode) {
    attributeMode.value = mode === 'attribute';
    if (!attributeMode.value) draft.value.attribute = '';
}

function addCase() {
    const ids = draft.value.cases.map(item => Number(String(item.id).replace(/^c/, '')) || 0);
    draft.value.cases.push({ id: `c${Math.max(0, ...ids) + 1}`, operator: '==', value: '' });
}

function moveUp(index) {
    const cases = draft.value.cases;
    cases.splice(index - 1, 2, cases[index], cases[index - 1]);
}

const bindingLabel = binding => `${getHassNodeSchema(binding.type)?.label ?? binding.type} · ${binding.data.property || '(no property)'}`;

/** The bindings wired to each output of this switch, with their case wire; Else also lists ungated ones. */
const bindingsByPort = computed(() => {
    const byPort = {};
    for (const binding of getSwitchBindings(props.graph, props.node.id)) {
        const wire = getWiresInto(props.graph, { node: binding.id, port: 'case' })[0];
        (byPort[getBindingCase(props.graph, binding.id).port] ??= []).push({ node: binding, wire, label: bindingLabel(binding) });
    }
    for (const binding of getSwitchFallbackBindings(props.graph, props.node.id)) {
        (byPort[SWITCH_ELSE_PORT] ??= []).push({ node: binding, wire: null, label: `${bindingLabel(binding)} (no case)` });
    }
    return byPort;
});
</script>
